import { Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ExtractionRequestService,
  GetExtractionRequestsDto
} from '../services/extraction-request.service';
import { ExtractionRequest, Pagination } from './types';
import { BankAccountsStore } from './bank-accounts.store';

@Injectable({
  providedIn: 'root'
})
export class ExtractionRequestsStore {
  loading = signal(false);
  loadingDescription = signal('');
  extractionRequests = signal<ExtractionRequest[]>([]);
  pagination = signal<Pagination>(null);
  paginationInput = signal<{ page: number; limit: number }>({ page: 1, limit: 50 });
  currentFilter = signal<GetExtractionRequestsDto>({});

  constructor(
    private extractionRequestService: ExtractionRequestService,
    private bankAccountsStore: BankAccountsStore,
    private matSnackBar: MatSnackBar
	) {}

	clearFilters() {
		this.currentFilter.set({})
	}
	
  async loadExtractionRequests(filter: GetExtractionRequestsDto = {}) {
    // Merge with existing filter and account_number
    const mergedFilter = { 
      ...filter, 
      account_number: this.bankAccountsStore.selectedBankAccountId() 
    };
    
    // Store the filter for future use (excluding account_number which is always added)
    this.currentFilter.set(filter);

    this.loading.set(true);
    try {
      const response = await this.extractionRequestService.getExtractionRequests(
        this.paginationInput(),
        mergedFilter
      );
      if (response.data?.elements) {
        const { elements, pagination } = response.data;

        // Only append if we're loading more pages (page > 1), otherwise replace
        if (this.paginationInput().page > 1 && this.extractionRequests().length > 0) {
          this.extractionRequests.set([...this.extractionRequests(), ...elements]);
        } else {
          this.extractionRequests.set(elements as ExtractionRequest[]);
        }
        this.pagination.set(pagination);

        return this.extractionRequests();
      } else if (response.error) {
        this.showLoadError(response.error?.message);
      }
    } catch (error) {
      this.showLoadError();
    } finally {
      this.loading.set(false);
    }
    return null;
  }

  reloadExtractionRequests() {
    this.paginationInput.set({ page: 1, limit: 50 });
    this.extractionRequests.set([]);
    this.loadExtractionRequests(this.currentFilter());
  }

  loadMore() {
    if (this.pagination()?.hasNextPage) {
      this.paginationInput.update((current) => ({
        ...current,
        page: current.page + 1
      }));
      this.loadExtractionRequests(this.currentFilter());
    }
  }

  showLoadError(message?: string) {
    this.matSnackBar.open(message || 'Error al cargar solicitudes de pago', 'Cerrar', {
      duration: 3000,
      panelClass: 'mat-snack-error'
    });
  }

  /**
   * Clears specific filter field and reloads extraction requests
   */
  clearFilterField(fieldName: keyof GetExtractionRequestsDto) {
    const currentFilterValue = this.currentFilter();
    const newFilter = { ...currentFilterValue };
    delete newFilter[fieldName];
    this.currentFilter.set(newFilter);
    this.reloadExtractionRequests();
	}
	
	clearAllFilters() {
		this.currentFilter.set({});
		this.reloadExtractionRequests();
	}

	/**
	 * Clear all filters without reloading (used when navigating away or logging out)
	 */
	clearFiltersOnly(): void {
		this.currentFilter.set({});
	}

  /**
   * Clears all extraction requests data from the store
   * Used when a user logs out to ensure no data remains in memory
   */
  clearStore() {
    this.loading.set(false);
    this.loadingDescription.set('');
    this.extractionRequests.set([]);
    this.pagination.set(null);
    this.currentFilter.set({});
  }
}
